import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Phone, Mail, MapPin, MessageCircle, Send, 
  Clock, CheckCircle, Loader2, User, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  sanitizeInput, 
  isValidEmail, 
  isValidPhone, 
  isSpamContent, 
  hasXssAttempt,
  isRateLimited,
  isHoneypotFilled,
  isSubmissionTooFast
} from '../utils/security';

const API_BASE = window.location.origin || '';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [honeypot, setHoneypot] = useState(''); // Bot trap
  const [formLoadTime] = useState(Date.now()); // Track form load time
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input on change
    const sanitized = sanitizeInput(value);
    setFormData(prev => ({ ...prev, [name]: sanitized }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // === SECURITY CHECKS ===
    
    // 1. Honeypot check (bot detection)
    if (isHoneypotFilled(honeypot)) {
      console.warn('Bot detected via honeypot');
      // Fake success to confuse bots
      setSubmitted(true);
      return;
    }
    
    // 2. Submission timing check (bot detection)
    if (isSubmissionTooFast(formLoadTime, 3)) {
      toast.error('Please take your time filling the form');
      return;
    }
    
    // 3. Rate limiting check
    if (isRateLimited('contact_form', 3, 60000)) {
      toast.error('Too many submissions. Please wait a minute.');
      return;
    }
    
    // 4. XSS attempt check
    const allValues = Object.values(formData).join(' ');
    if (hasXssAttempt(allValues)) {
      toast.error('Invalid characters detected. Please remove special characters.');
      return;
    }
    
    // 5. Spam content check
    if (isSpamContent(formData.message) || isSpamContent(formData.subject)) {
      toast.error('Your message was flagged as spam. Please revise.');
      return;
    }
    
    // === VALIDATION ===
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Name length check
    if (formData.name.length < 2 || formData.name.length > 100) {
      toast.error('Name must be between 2 and 100 characters');
      return;
    }
    
    // Email validation
    if (!isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Phone validation (if provided)
    if (formData.phone && !isValidPhone(formData.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    // Message length check
    if (formData.message.length < 10 || formData.message.length > 5000) {
      toast.error('Message must be between 10 and 5000 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          _timestamp: formLoadTime, // For server-side timing check
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        toast.success('Message sent successfully!');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-20 md:pt-24 pb-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-zinc-900 to-black py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Have questions about our premium transfer services? We're here to help 24/7.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {/* Phone */}
              <Card className="bg-zinc-900 border-zinc-800 hover:border-[#FF8C00]/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#FF8C00]/10 rounded-lg">
                      <Phone className="h-6 w-6 text-[#FF8C00]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Call Us</h3>
                      <a 
                        href="tel:+905352385797" 
                        className="text-gray-400 hover:text-[#FF8C00] transition-colors"
                      >
                        +90 535 238 5797
                      </a>
                      <p className="text-xs text-gray-500 mt-1">Available 24/7</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="bg-zinc-900 border-zinc-800 hover:border-[#FF8C00]/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#FF8C00]/10 rounded-lg">
                      <Mail className="h-6 w-6 text-[#FF8C00]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Email Us</h3>
                      <a 
                        href="mailto:info@goturkeytransfer.com" 
                        className="text-gray-400 hover:text-[#FF8C00] transition-colors"
                      >
                        info@goturkeytransfer.com
                      </a>
                      <p className="text-xs text-gray-500 mt-1">We reply within 2 hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp */}
              <Card className="bg-zinc-900 border-zinc-800 hover:border-green-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <MessageCircle className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">WhatsApp</h3>
                      <a 
                        href="https://wa.me/905352385797" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-green-500 transition-colors"
                      >
                        +90 535 238 5797
                      </a>
                      <p className="text-xs text-gray-500 mt-1">Quick responses</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Working Hours */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#FF8C00]/10 rounded-lg">
                      <Clock className="h-6 w-6 text-[#FF8C00]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Working Hours</h3>
                      <p className="text-gray-400 text-sm">24 Hours / 7 Days</p>
                      <p className="text-xs text-gray-500 mt-1">Including holidays</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company Info */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#FF8C00]/10 rounded-lg">
                      <MapPin className="h-6 w-6 text-[#FF8C00]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">VIPGO TECH LTD</h3>
                      <p className="text-gray-400 text-sm">Company No: 17039074</p>
                      <div className="text-xs text-gray-500 mt-2 leading-relaxed">
                        <p>Unit 501 Leroy House</p>
                        <p>434–436 Essex Road</p>
                        <p>London, N1 3FY</p>
                        <p>United Kingdom</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="h-8 w-8 text-green-500" />
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2">Message Sent!</h2>
                      <p className="text-gray-400 mb-6">
                        Thank you for contacting us. We'll get back to you within 2 hours.
                      </p>
                      <Button
                        onClick={() => setSubmitted(false)}
                        className="bg-[#FF8C00] hover:bg-[#E67E00]"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="mb-8">
                        <h2 className="text-2xl font-bold text-white mb-2">Send Us a Message</h2>
                        <p className="text-gray-400">
                          Fill out the form below and we'll respond as soon as possible.
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Honeypot field - hidden from users, bots will fill it */}
                        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                          <label htmlFor="website_url">Website URL (Leave empty)</label>
                          <input
                            type="text"
                            id="website_url"
                            name="website_url"
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                            tabIndex={-1}
                            autoComplete="off"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Name */}
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Full Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                              <Input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="John Doe"
                                className="bg-zinc-800 border-zinc-700 text-white pl-10 h-12"
                                required
                                maxLength={100}
                                data-testid="contact-name"
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                              <Input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="john@example.com"
                                className="bg-zinc-800 border-zinc-700 text-white pl-10 h-12"
                                required
                                maxLength={254}
                                data-testid="contact-email"
                              />
                            </div>
                          </div>

                          {/* Phone */}
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Phone Number
                            </label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                              <Input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+90 555 123 4567"
                                className="bg-zinc-800 border-zinc-700 text-white pl-10 h-12"
                                maxLength={20}
                                data-testid="contact-phone"
                              />
                            </div>
                          </div>

                          {/* Subject */}
                          <div>
                            <label className="block text-sm font-medium text-white mb-2">
                              Subject
                            </label>
                            <div className="relative">
                              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                              <Input
                                type="text"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder="Booking inquiry"
                                className="bg-zinc-800 border-zinc-700 text-white pl-10 h-12"
                                data-testid="contact-subject"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Message */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Message <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="How can we help you?"
                            rows={6}
                            className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-[#FF8C00] resize-none"
                            required
                            data-testid="contact-message"
                          />
                        </div>

                        {/* Submit Button */}
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full h-12 bg-[#FF8C00] hover:bg-[#E67E00] text-white font-semibold"
                          data-testid="contact-submit"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="h-5 w-5 mr-2" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Quick Info */}
              <div className="mt-6 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <p className="text-sm text-gray-400 text-center">
                  🚗 For immediate assistance with your transfer, call us directly at{' '}
                  <a href="tel:+905352385797" className="text-[#FF8C00] font-medium">
                    +90 535 238 5797
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
